// ============================================================================
// Escalation actions — the post-placement mutation surface. A single modal-driven
// dropdown, reused at every level (order / line / allocation / stage). Each action
// declares the fields it collects and the api call it runs; on success the endpoint
// returns the refreshed SalesOrderDetail, which we hand back via onDone (single
// source of truth — no separate refetch). All rules live server-side; the UI just
// collects fields and surfaces the result.
// ============================================================================
import {
  Alert,
  Button,
  Checkbox,
  DatePicker,
  Drawer,
  Dropdown,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Switch,
  Tooltip,
  Typography,
  message,
} from "antd";
import type { SizeType } from "antd/es/config-provider/SizeContext";
import dayjs, { Dayjs } from "dayjs";
import React, { useEffect, useState } from "react";
import BillingReconcileModal from "./billing_reconcile_modal";
import ModalBody, { FLUSH_MODAL, MODAL_BODY_STYLE } from "./modal_body";
import { ChargeDetailsDrawer } from "../../../components/pro_forma_invoice/charge_details_drawer";
import { WAREHOUSES } from "../../../interfaces/warehouse.interface";
import { usePermissions } from "../../../custom_hooks/permission/permission_hook";
import { PermissionName } from "../../../custom_hooks/permission/permission_hook";
import { piEditLockReason } from "../../../helpers/estimate_edit_window";
import {
  addUnloadingCharge,
  cancelOrder,
  changeAddress,
  deallocateAllocation,
  modifyPo,
  getRedeliveryOptions,
  pickComplete,
  reattemptDelivery,
  resendSupplierNotification,
  rescheduleDelivery,
  reschedulePickup,
  retryStage,
  settleInvoiceRounding,
  serviceComplete,
  DEALLOCATION_REASONS,
  REFUND_REASONS,
  refundLine,
  getDeallocationOptions,
  syncOrderItems,
  upgradeP2P,
  upgradeExpress,
} from "../oms.api";
import type { DeallocationItemOptions } from "../oms.api";
import {
  Allocation,
  AllocationItem,
  EstimateLine,
  OperationStage,
  ProductInfo,
  ReattemptWarehouse,
  RedeliveryOptions,
  SalesOrderDetail,
  SalesOrderLine,
  SourcingOptions,
} from "../types";

// In-transit reattempt guard. When the reattempt endpoint 409s with requires_confirmation, the
// operator must confirm pulling the order off its live truck AND type a reason — it's sent to the
// delivery team and drives the backend's WhatsApp escalation, so it's mandatory here. The textarea
// owns its own state and reports every keystroke up to the captured `reason` the modal's OK reads.
const InTransitReason: React.FC<{
  detail: string;
  onChange: (v: string) => void;
}> = ({ detail, onChange }) => {
  const [value, setValue] = useState("");
  return (
    <div>
      <p style={{ marginTop: 0 }}>{detail}</p>
      <Input.TextArea
        rows={3}
        autoFocus
        placeholder="Reason (sent to the delivery team) — required"
        value={value}
        onChange={(e) => {
          setValue(e.target.value);
          onChange(e.target.value);
        }}
      />
    </div>
  );
};

// Resolves to the trimmed reason once the operator confirms, or null if they back out. Keeps the
// modal open (and nags) until a non-empty reason is entered.
function confirmInTransitReattempt(detail: string): Promise<string | null> {
  return new Promise((resolve) => {
    let reason = "";
    Modal.confirm({
      title: "Order is in transit",
      width: 460,
      okText: "Yes, mark reattempt",
      cancelText: "Cancel",
      okButtonProps: { danger: true },
      content: <InTransitReason detail={detail} onChange={(v) => (reason = v)} />,
      onOk: () => {
        if (!reason.trim()) {
          message.error("A reason is required to reattempt an in-transit order");
          return Promise.reject(); // keep the modal open
        }
        resolve(reason.trim());
      },
      onCancel: () => resolve(null),
    });
  });
}

type FieldKey =
  | "qty"
  | "reason"
  | "restock"
  | "reason_code"
  | "refund_reason_code"
  | "deallocation_items"
  | "measured_items"
  | "redelivery_items"
  | "return_wh_id"
  | "current_wh_id"
  | "target_date"
  | "reattempt_date"
  | "availability_date"
  | "pickup_date"
  | "notes";

// A day picker with past days disabled — every date move is refused server-side for a past day, so
// it is blocked here rather than left to come back as a 400.
// The value is a Dayjs — `run` formats it. `required` is false for an OPTIONAL date (reattempt's
// planned day, which defaults to the earliest slot when left blank).
const dateField = (
  name: string,
  label: string,
  tooltip: string,
  required: boolean = true
): React.ReactNode => (
  <Form.Item
    key={name}
    name={name}
    label={label}
    tooltip={tooltip}
    rules={required ? [{ required: true, message: `${label} is required` }] : undefined}
  >
    <DatePicker
      style={{ width: "100%" }}
      format="YYYY-MM-DD"
      allowClear={!required}
      disabledDate={(d: Dayjs) => !!d && d < dayjs().startOf("day")}
    />
  </Form.Item>
);

// Dayjs → the ISO day string the API takes. Undefined-safe so a cleared picker surfaces as the
// backend's "date required" 400 rather than a client-side crash.
const isoDay = (d?: Dayjs): string => (d ? d.format("YYYY-MM-DD") : "");

// A warehouse dropdown. Shared by the return's `return_wh_id` (where returned goods land) and
// reattempt's `current_wh_id` (where failed-delivery goods currently sit). The form field `name`
// matches the API param so it can be passed straight through in `run`.
//
// `options` overrides the MD-warehouse constant when the backend supplied a wider list for this
// allotment — the reattempt also offers the supplier's godowns, which no static list can know.
const warehouseField = (
  name: string,
  label: string,
  tooltip: string,
  required: boolean,
  options?: ReattemptWarehouse[]
): React.ReactNode => {
  // Grouped so a supplier's godown is never mistaken for one of ours at a glance. Two vendors can
  // name a godown the same thing, so a supplier row carries its vendor and city.
  const group = (heading: string, kind: ReattemptWarehouse["kind"], rows: ReattemptWarehouse[]) => ({
    label: heading,
    options: rows
      .filter((w) => w.kind === kind)
      .map((w) => ({
        value: w.id,
        label: kind === "md_wh" ? w.label : [w.vendor, w.label, w.city].filter(Boolean).join(" · "),
      })),
  });
  const groups = options?.length
    ? [
        group("Our warehouses", "md_wh", options),
        group("Supplier warehouses", "vendor_wh", options),
      ].filter((g) => g.options.length)
    : [{ label: "Our warehouses", options: WAREHOUSES.map((w) => ({ value: w.id, label: w.label })) }];
  return (
    <Form.Item
      key={name}
      name={name}
      label={label}
      tooltip={tooltip}
      rules={required ? [{ required: true, message: `${label} is required` }] : undefined}
    >
      <Select
        showSearch
        allowClear={!required}
        optionFilterProp="label"
        placeholder="Choose a warehouse"
        options={groups}
      />
    </Form.Item>
  );
};

interface ActionDef {
  key: string;
  label: string;
  danger?: boolean;
  confirm?: string;
  // The permission that unlocks this action. Defaults to "admin" — set it to widen a
  // single action to more roles (e.g. "Postpone delivery" → post_sales) without opening
  // the rest of the menu.
  permission?: PermissionName;
  fields: FieldKey[];
  // Most actions resolve to the refreshed SalesOrderDetail (handed to onDone). An action with no
  // SO to refresh — e.g. a fire-and-inform resend that toasts its own result — resolves to `true`
  // to close the modal without touching order state; `undefined` leaves the modal open on failure.
  // Absent on a `custom` action, whose own surface runs the mutation.
  run?: (values: Record<string, any>) => Promise<SalesOrderDetail | true | undefined>;
  // Escape hatch for an action whose form outgrew the shared field modal (the charge drawer with
  // its payment-collection flow). Picking it invokes this instead of opening the modal; the same
  // permission filter still applies.
  custom?: () => void;
  // Admin-only from 10 PM to 8 AM IST (holders of pi.edit_after_hours); the backend enforces it,
  // this only greys the item out with the reason.
  overnightLocked?: boolean;
}

// Context a field may need to render a human-choosable control rather than a raw id box.
interface FieldContext {
  estimateLines?: EstimateLine[];
  // The allotment's items + the product/unit lookups the parent card already holds, so the
  // deallocation picker can name each row by product instead of by allocation-item id.
  allocationItems?: AllocationItem[];
  lineProducts?: Map<number, ProductInfo | null>;
  lineUnits?: Map<number, string | null>;
  // On a DROP SHIPMENT each LINE is all-or-nothing (its PO line, the customer's quantity and the one
  // booked pickup are the same units — the backend refuses a part-line). Other lines on the same
  // allotment can stay, so each row becomes a checkbox for its full quantity rather than a qty box:
  // the operator chooses WHICH lines come off, never how much of one.
  wholeLinesOnly?: boolean;
  // Where each allotment item may be unwound TO, keyed by allocation item id. Loaded when the
  // deallocation action opens — the legitimate places depend on how far these particular goods have
  // got, which only the backend knows.
  destinations?: Map<number, DeallocationItemOptions>;
  // The candidate suppliers + warehouses for the line being reallocated (from `getSourcingOptions`),
  // so the "Re-source from supplier" picker offers real vendors instead of an id box.
  sourcingOptions?: SourcingOptions;
  // What a reattempt would do for this allotment (from `getRedeliveryOptions`) — which mode the
  // backend will take, and what is still available to send out again. Undefined while it loads.
  redelivery?: RedeliveryOptions;
}

const fieldItem = (key: FieldKey, ctx?: FieldContext): React.ReactNode => {
  switch (key) {
    case "qty":
      return (
        <Form.Item key={key} name="qty" label="Quantity" rules={[{ required: true, message: "qty required" }]}>
          <InputNumber min={0.01} step={1} style={{ width: "100%" }} />
        </Form.Item>
      );
    case "reason_code":
      // WHY the quantity is coming off. The queryable half of the record — a free-text note cannot
      // answer "how much did we buy but not sell, and why", and the damage codes are what replaced
      // the old Record-damage action.
      return (
        <Form.Item
          key={key}
          name="reason_code"
          label="Why is this coming off?"
          rules={[{ required: true, message: "Pick a reason" }]}
          tooltip="Pick the closest match. The damage reasons are the ones that leave the purchase order standing — we bought those units and still owe for them."
        >
          <Select options={DEALLOCATION_REASONS} placeholder="Select a reason" />
        </Form.Item>
      );
    case "refund_reason_code":
      // The refund exits have their own vocabulary — a deallocation reason answers "why did the
      // goods come off", which is a different question from "why is the customer getting paid back".
      return (
        <Form.Item
          key={key}
          name="reason_code"
          label="Why is the money going back?"
          rules={[{ required: true, message: "Pick a reason" }]}
        >
          <Select options={REFUND_REASONS} placeholder="Select a reason" />
        </Form.Item>
      );
    case "redelivery_items": {
      // Only the REDELIVERY mode picks items: a re-point moves the whole undelivered load and has
      // nothing to choose. So this renders as a short note in repoint mode, and while the backend is
      // still being asked which mode applies.
      const opts = ctx?.redelivery;
      if (!opts) {
        return (
          <Typography.Paragraph key={key} type="secondary" style={{ fontSize: 12 }}>
            Checking what this delivery still owes the customer…
          </Typography.Paragraph>
        );
      }
      if (opts.mode !== "redelivery") {
        return (
          <Alert
            key={key}
            type="info"
            showIcon
            style={{ marginBottom: 12 }}
            message="This delivery has not arrived yet"
            description="The whole load is re-shipped from the warehouse you pick below, and everything else on the same truck moves with it. There is nothing to choose item by item."
          />
        );
      }
      // Prefilled at the full remainder — the load usually comes back whole, and trimming a row is
      // quicker than adding every one. A row set to 0 (or cleared) is left with the customer.
      return (
        <Form.Item
          key={key}
          label="What goes back out"
          tooltip="These goods were delivered but came back on the truck. Quantities are the ones this leg shipped, which is the most that can go out again — nothing is netted off, so the same goods can be sent out as many times as they come back."
          required
          extra="Trim a row to what actually came back; set it to 0 to leave that item with the customer."
        >
          {opts.items.map((item) => {
            const product = ctx?.lineProducts?.get(item.sales_order_line_id) ?? null;
            const unit = ctx?.lineUnits?.get(item.sales_order_line_id) ?? "";
            const name = product?.product_name || `SO line #${item.sales_order_line_id}`;
            return (
              <div
                key={item.allocation_item_id}
                style={{
                  border: "1px solid #f0f0f0",
                  borderRadius: 6,
                  padding: "8px 10px",
                  marginBottom: 8,
                }}
              >
                <Typography.Text style={{ fontSize: 12, fontWeight: 500 }}>
                  {name.length > 60 ? `${name.slice(0, 60)}…` : name}
                </Typography.Text>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {" "}
                  · shipped {item.qty}
                  {unit ? ` ${unit}` : ""}
                </Typography.Text>
                <Form.Item
                  name={["redelivery_picks", String(item.allocation_item_id)]}
                  noStyle
                  initialValue={Number(item.qty)}
                >
                  <InputNumber
                    min={0}
                    max={Number(item.qty)}
                    step={1}
                    style={{ width: 130, marginTop: 6 }}
                    placeholder="qty out again"
                  />
                </Form.Item>
              </div>
            );
          })}
        </Form.Item>
      );
    }
    case "deallocation_items": {
      // The one multi-row field: a deallocation acts on the WHOLE allotment, so the operator picks a
      // quantity per item in a single pass rather than repeating a single-qty action per line. A row
      // left blank is untouched — only rows with a quantity are sent.
      const items = ctx?.allocationItems ?? [];
      const whole = !!ctx?.wholeLinesOnly;
      return (
        <Form.Item
          key={key}
          label="What comes off this allotment"
          tooltip="Quantity is what LEAVES this allotment and goes back to the availability desk. Leave a row blank to leave that item alone. This does not touch the customer's order — cancel the quantity separately once it is off."
          required
          extra={
            whole
              ? "This is a drop shipment: for each line the supplier's PO line, the customer's quantity and the one booked pickup are the same units, so a line comes off WHOLE or not at all. Tick the lines to take off — the rest of the allotment is untouched."
              : undefined
          }
        >
          {items.map((item) => {
            const product = ctx?.lineProducts?.get(item.sales_order_line_id) ?? null;
            const unit = ctx?.lineUnits?.get(item.sales_order_line_id) ?? "";
            const name = product?.product_name || `SO line #${item.sales_order_line_id}`;
            const blocked = ctx?.destinations?.get(item.id)?.blocked ?? null;
            return (
              <div
                key={item.id}
                style={{
                  border: "1px solid #f0f0f0",
                  borderRadius: 6,
                  padding: "8px 10px",
                  marginBottom: 8,
                }}
              >
                <Typography.Text style={{ fontSize: 12, fontWeight: 500 }}>
                  {name.length > 60 ? `${name.slice(0, 60)}…` : name}
                </Typography.Text>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {" "}
                  · allotted {item.qty}
                  {unit ? ` ${unit}` : ""}
                </Typography.Text>
                <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 6 }}>
                  {/* A line that can only come off WHOLE is a yes/no, not a quantity — so it gets a
                      checkbox rather than a locked box pre-filled with its full qty. Pre-filling every
                      row made the choice all-or-nothing across the allotment: there was no way to take
                      one product off a two-product drop shipment. Unticked rows carry no qty and the
                      submit handler drops them. */}
                  {whole ? (
                    // A plain boolean, deliberately: mapping the tick straight onto `qty` needed
                    // getValueProps + getValueFromEvent, and the pair left the box unclickable. The
                    // submit handler turns `take` into the line's full quantity instead.
                    <Form.Item
                      name={["picks", String(item.id), "take"]}
                      noStyle
                      valuePropName="checked"
                    >
                      <Checkbox disabled={!!blocked} style={{ width: 150 }}>
                        Take off {item.qty}
                        {unit ? ` ${unit}` : ""}
                      </Checkbox>
                    </Form.Item>
                  ) : (
                    <Form.Item name={["picks", String(item.id), "qty"]} noStyle>
                      <InputNumber
                        min={0}
                        max={Number(item.qty)}
                        step={1}
                        disabled={!!blocked}
                        style={{ width: 110 }}
                        placeholder="qty off"
                      />
                    </Form.Item>
                  )}
                  {/* WHERE the goods come to rest — the answer that decides how far the unwind runs.
                      Only the places these goods have actually been are offered, so there is nothing
                      to get wrong: back to the supplier rolls the purchase order back with them,
                      one of our warehouses leaves it standing.

                      An item with nowhere to go carries `blocked` instead: the deallocation refuses
                      on that ground, so state the reason rather than showing a picker that can only
                      earn a 400 on confirm. */}
                  {blocked ? (
                    <Typography.Text type="danger" style={{ flex: 1, fontSize: 12 }}>
                      {blocked}
                    </Typography.Text>
                  ) : (
                    <Form.Item
                      name={["picks", String(item.id), "destination"]}
                      noStyle
                      initialValue={ctx?.destinations?.get(item.id)?.default ?? undefined}
                    >
                      <Select
                        style={{ flex: 1, minWidth: 230 }}
                        placeholder="Where do the goods end up?"
                        options={(ctx?.destinations?.get(item.id)?.options ?? []).map((o) => ({
                          value: o.value,
                          label: o.label,
                        }))}
                      />
                    </Form.Item>
                  )}
                </div>
              </div>
            );
          })}
        </Form.Item>
      );
    }
    case "measured_items": {
      // One measured quantity per item. Deliberately NOT the deallocation shape: there is no
      // destination to pick (nothing comes off the allotment and nothing goes back to the desk) and
      // the number is ABSOLUTE — what the purchase order should now stand at — not an amount to
      // subtract. Each box is pre-filled with what the PO stands at today, so an operator who only
      // meant to change one line does not silently blank the others.
      const items = ctx?.allocationItems ?? [];
      return (
        <Form.Item
          key={key}
          label="What the supplier actually measured"
          tooltip="The quantity to put ON THE PURCHASE ORDER. It replaces what is there — it is not an amount to take off — and may be higher or lower than what was ordered. The customer's quantity is untouched. Each box starts at what the PO says today; confirming it unchanged is still worth doing, because it re-states the goods-inwards legs to match — which is what lets the GRN through."
          required
        >
          {items.map((item) => {
            const product = ctx?.lineProducts?.get(item.sales_order_line_id) ?? null;
            const unit = ctx?.lineUnits?.get(item.sales_order_line_id) ?? "";
            const name = product?.product_name || `SO line #${item.sales_order_line_id}`;
            const onPo = item.po_qty ?? item.qty;
            return (
              <div
                key={item.id}
                style={{
                  border: "1px solid #f0f0f0",
                  borderRadius: 6,
                  padding: "8px 10px",
                  marginBottom: 8,
                }}
              >
                <Typography.Text style={{ fontSize: 12, fontWeight: 500 }}>
                  {name.length > 60 ? `${name.slice(0, 60)}…` : name}
                </Typography.Text>
                {/* Both numbers, always — the whole point of the action is that they may differ, and
                    an operator who cannot see the gap cannot judge whether it is the right one. */}
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {" "}
                  · sold to customer {item.qty}
                  {unit ? ` ${unit}` : ""} · on the PO {onPo}
                  {unit ? ` ${unit}` : ""}
                </Typography.Text>
                <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 6 }}>
                  <Form.Item
                    name={["measured", String(item.id)]}
                    noStyle
                    initialValue={Number(onPo)}
                  >
                    <InputNumber
                      min={0}
                      step={1}
                      style={{ width: 140 }}
                      placeholder="measured qty"
                    />
                  </Form.Item>
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    goes on the purchase order
                  </Typography.Text>
                </div>
              </div>
            );
          })}
        </Form.Item>
      );
    }
    case "return_wh_id":
      return warehouseField(
        "return_wh_id",
        "Return warehouse",
        "Where the returned goods physically land. A dispatched shipment may be dropped at an unexpected warehouse; leave blank to default to where the order shipped from. Only used when goods come back.",
        false
      );
    case "target_date":
      return dateField(
        "target_date",
        "New delivery date",
        "The day the customer now wants the goods — earlier or later. Only the delivery leg moves. An earlier date must leave the SLA its full time (no squeezing our handling) — to go faster, express the order or convert it to dropship. On a dropship the SLA counts from the vendor's ready date."
      );
    case "reattempt_date":
      return dateField(
        "reattempt_date",
        "Planned delivery date",
        "Optional — the day to run the delivery. The carrier is held to it (goods wait rather than arrive early). Leave blank to book the earliest slot (today).",
        false
      );
    case "availability_date":
      return dateField(
        "new_date",
        "Goods ready on",
        "The day the goods will now be available — earlier or later. Every open leg is re-planned from it per the SLA, and the delivery date moves with it: later if it can no longer be made, earlier when the goods are ready sooner. A delivery the customer postponed is never brought in before the date they asked for."
      );
    case "pickup_date":
      return dateField(
        "pickup_date",
        "Return pickup date",
        "The day the carrier should collect the returned goods from the customer. Stamped onto the return pickup stage and used as the Loginext pickup window start. For an outstation customer an earlier day than D+3 (working days) moves to D+3."
      );
    case "current_wh_id":
      return warehouseField(
        "current_wh_id",
        "Current warehouse",
        "Where the goods physically sit right now — the warehouse the failed attempt parked them at, or the one the truck brought them back to. The supplier's own godowns are offered too: a drop shipment never passed through one of ours, so a failed attempt parks it back with the vendor. The delivery goes out from here.",
        true,
        ctx?.redelivery?.warehouses
      );
    case "restock":
      return (
        <Form.Item
          key={key}
          name="restock"
          label="Bring goods back to warehouse"
          valuePropName="checked"
          tooltip="On: a return pickup is booked and the goods are received back into the warehouse. Off: credit-only — nothing is picked up, and the Zoho sales return is closed on creation."
        >
          <Switch />
        </Form.Item>
      );
    case "notes":
      return (
        <Form.Item key={key} name="notes" label="Notes">
          <Input.TextArea
            rows={2}
            placeholder="Optional — who carried out the work, actual area covered, anything worth recording"
          />
        </Form.Item>
      );
    case "reason":
    default:
      return (
        <Form.Item key={key} name="reason" label="Reason">
          <Input.TextArea rows={2} placeholder="Optional note for the escalation ledger" />
        </Form.Item>
      );
  }
};

const EscalationMenu: React.FC<{
  actions: ActionDef[];
  onDone: (so: SalesOrderDetail) => void;
  label?: string;
  size?: SizeType;
  fieldContext?: FieldContext;
  // The line whose candidate suppliers feed the "supplier" picker (reallocation vendor override).
  // Fetched lazily the first time a supplier-field action is opened, so no cost on rows never acted on.
  // The allotment whose reattempt mode + remaining items are fetched when the reattempt action is
  // opened. Absent on the stage-level menu, which offers no reattempt.
  redeliveryAllocationId?: number;
  // The allotment whose per-item resting places the deallocation picker loads.
  deallocationAllocationId?: number;
}> = ({
  actions,
  onDone,
  label = "Actions",
  size,
  fieldContext,
  redeliveryAllocationId,
  deallocationAllocationId,
}) => {
  const { hasPermission } = usePermissions();
  const overnightLock = piEditLockReason(hasPermission);
  const [active, setActive] = useState<ActionDef | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // Cached candidate suppliers, tagged with the line they were fetched for — the "Which item" picker
  // can change the line mid-form, and offering the wrong line's vendors is worse than an id box.
  // The item chosen in the "Which item" field, when the action offers one (multi-line allotment).
  // What a reattempt would do for this allotment, tagged with the allocation it was fetched for.
  const [redelivery, setRedelivery] = useState<
    { allocationId: number; opts: RedeliveryOptions } | undefined
  >();
  const [form] = Form.useForm();

  useEffect(() => {
    if (active) {
      form.resetFields();
      setRedelivery(undefined);
    }
  }, [active, form]);

  // Where each item may be unwound to — loaded only when the deallocation action is actually
  // opened, since it costs a round trip and most actions never need it. Re-fetched each open: the
  // legitimate places move as the goods do.
  const [destinations, setDestinations] = useState<Map<number, DeallocationItemOptions>>();
  useEffect(() => {
    if (active?.fields.includes("deallocation_items") && deallocationAllocationId) {
      getDeallocationOptions(deallocationAllocationId).then((rows) => {
        if (rows) setDestinations(new Map(rows.map((r) => [r.allocation_item_id, r])));
      });
    }
  }, [active, deallocationAllocationId]);

  // Ask the backend which mode a reattempt would take (and what is still owed) only when the operator
  // actually opens the action — most allotments are never reattempted. The answer moves as the order
  // does, so it is re-fetched each time the action opens rather than cached for the session.
  useEffect(() => {
    if (
      active?.fields.includes("redelivery_items") &&
      redeliveryAllocationId &&
      redelivery?.allocationId !== redeliveryAllocationId
    ) {
      getRedeliveryOptions(redeliveryAllocationId).then(
        (opts) => opts && setRedelivery({ allocationId: redeliveryAllocationId, opts })
      );
    }
  }, [active, redeliveryAllocationId, redelivery]);

  if (actions.length === 0) return null;
  // Post-placement mutations require oms.update by default; an action may declare its own
  // permission to widen it (e.g. "Reschedule delivery" → oms.postpone_delivery). Keep only
  // what this user may run — if nothing is left, the menu doesn't render at all.
  const permitted = actions.filter((a) => hasPermission(a.permission ?? "oms.update"));
  if (permitted.length === 0) return null;

  const ctx: FieldContext = {
    ...fieldContext,
    redelivery: redelivery?.opts,
    destinations,
  };

  const submit = async () => {
    const values = await form.validateFields();
    setSubmitting(true);
    const result = await active!.run?.(values);
    setSubmitting(false);
    if (result === true) {
      setActive(null); // success with nothing to refresh (the action toasted its own result)
    } else if (result) {
      onDone(result);
      setActive(null);
    }
  };

  return (
    <>
      <Dropdown
        trigger={["click"]}
        menu={{
          items: permitted.map((a) => {
            const locked = a.overnightLocked && overnightLock;
            return {
              key: a.key,
              label: locked ? <Tooltip title={overnightLock}>{a.label}</Tooltip> : a.label,
              danger: a.danger,
              disabled: Boolean(locked),
            };
          }),
          onClick: ({ key }) => {
            const action = permitted.find((a) => a.key === key) || null;
            if (action?.custom) action.custom();
            else setActive(action);
          },
        }}
      >
        <Button size={size}>{label} ▾</Button>
      </Dropdown>
      <Modal
        title={active?.label}
        open={!!active}
        onCancel={() => setActive(null)}
        onOk={submit}
        okText="Confirm"
        okButtonProps={{ danger: active?.danger, loading: submitting }}
        destroyOnClose
        centered
        className={FLUSH_MODAL}
        bodyStyle={MODAL_BODY_STYLE}
      >
        <ModalBody>
          {active?.confirm && <p style={{ color: "#888", marginTop: 0 }}>{active.confirm}</p>}
          <Form
            form={form}
            layout="vertical"
            initialValues={{ restock: true }}
          >
            {active?.fields.map((f) => fieldItem(f, ctx))}
          </Form>
        </ModalBody>
      </Modal>
    </>
  );
};

export const OrderActions: React.FC<{
  so: SalesOrderDetail;
  onDone: (so: SalesOrderDetail) => void;
}> = ({ so, onDone }) => {
  // The two charge actions collect an optional PAYMENT alongside the charge — the full drawer
  // flow the PI edit screen used (payment type / reference / branch / POS devices / PAN) — so
  // they open the charge drawer instead of the shared field modal. The charge itself still goes
  // through the OMS escalation endpoints; only the payment recording reuses the estimate-level
  // transaction APIs, which is where order payments live anyway.
  const [chargeDrawer, setChargeDrawer] = useState<"unloading" | "express" | null>(null);
  // The billing reconcile is a CHECK first and a repair second, so it opens its own modal (the
  // document-by-document report) rather than the shared field modal, which has nothing to collect.
  const [billingOpen, setBillingOpen] = useState(false);
  return (
    <>
      <EscalationMenu
        onDone={onDone}
        actions={[
      {
        key: "cancel",
        overnightLocked: true,
        permission: "oms.cancel_order",
        label: "Cancel order",
        danger: true,
        confirm: "Stops open operations, voids un-pushed docs, closes demand and compensates.",
        fields: ["reason"],
        run: (v) => cancelOrder(so.id, v.reason || ""),
      },
      {
        // Not an escalation like its menu siblings — it mutates nothing about the order, only
        // re-pushes its product masters to Zoho. Lives here because it belongs to the ORDER and the
        // header has no room for another button. `run` returns true: the api reports per-item
        // outcomes in its own toast and there is no order state to refresh.
        key: "sync_items",
        overnightLocked: true,
        permission: "oms.sync_items",
        label: "Sync items to Zoho",
        confirm:
          "Re-push this order's products (name, SKU, unit, rates, HSN/GST) to their Zoho item masters. The backend does this automatically once, when the order is placed — use this after correcting a product that was already ordered.",
        fields: [],
        run: () => syncOrderItems(so.id),
      },
      {
        // Mutates no address and no amount: the estimate's billing address already decides who is
        // billed (its GSTIN → a B2B contact, none → B2C), and this makes the order's Zoho documents
        // agree with it. Its own modal reports which document is wrong before offering the repair.
        key: "reconcile_billing",
        overnightLocked: true,
        permission: "oms.reconcile",
        label: "Reconcile Zoho docs with billing address",
        fields: [],
        custom: () => setBillingOpen(true),
      },
      {
        key: "address",
        overnightLocked: true,
        permission: "oms.change_address",
        label: "Change shipping address",
        confirm:
          "Re-points every not-yet-delivered customer leg at the estimate's current ship address. Edit the address on the estimate first.",
        fields: ["reason"],
        run: (v) => changeAddress(so.id, v.reason || ""),
      },
      {
        key: "unloading-charge",
        overnightLocked: true,
        label: "Add unloading charge",
        fields: [],
        custom: () => setChargeDrawer("unloading"),
      },
      {
        // Same visibility as the per-allotment "P2P upgrade" (default admin). The only action that
        // marks the order express: the mode flips and every eligible backorder allotment re-routes.
        key: "express",
        overnightLocked: true,
        label: "Express this order",
        fields: [],
        custom: () => setChargeDrawer("express"),
      },
    ]}
  />
      <BillingReconcileModal
        salesOrderId={so.id}
        open={billingOpen}
        onClose={() => setBillingOpen(false)}
      />
      <Drawer
        title={chargeDrawer === "express" ? "Express this order" : "Add unloading charge"}
        open={!!chargeDrawer}
        onClose={() => setChargeDrawer(null)}
        width={480}
        destroyOnClose
      >
        {chargeDrawer && (
          <ChargeDetailsDrawer
            estimateId={so.estimate_id}
            chargeType={chargeDrawer === "express" ? "express_delivery" : "unloading"}
            title={chargeDrawer === "express" ? "Express Charge" : "Unloading Charge"}
            // An express upgrade works without a charge; the unloading action IS the charge.
            chargeRequired={chargeDrawer === "unloading"}
            showReason
            description={
              chargeDrawer === "express"
                ? "Express this entire order? The order's mode becomes express and every eligible backorder allotment is re-routed to ship direct from its supplier (dropship); ineligible ones are SKIPPED, not failed — per-allotment outcomes land in Escalations. The charge is optional (gross, incl. 18% GST) — leave it blank to upgrade without charging. Record the payment collected for it below, or leave the payment section empty."
                : "This SETS the order's unloading charge to the amount below (gross, incl. 18% GST) — resubmitting replaces the existing charge and moves the payable by the difference, it does not add on top. Raising the charge raises the payable: an unpaid order will hold at dispatch until the difference is collected. Record the payment collected for it below, or leave the payment section empty."
            }
            onAddCharge={async ({ amount, reason, payment }) => {
              const updated =
                chargeDrawer === "express"
                  ? await upgradeExpress(so.id, { charge_amount: amount, reason, payment })
                  : await addUnloadingCharge(so.id, { amount: amount ?? 0, reason, payment });
              if (!updated) return false; // refused — detail already toasted; keep the drawer up
              // The inline payment's fate, so the operator knows whether the cash log still owes
              // an authorize step (everything lands `authenticated` except Razorpay POS, which is
              // already settled at the device).
              const rp = updated.recorded_payment;
              if (rp) {
                message.success(
                  rp.status === "captured"
                    ? "Payment recorded — captured (settled at the device)"
                    : "Payment recorded — authenticated; verify and authorize it from the cash log"
                );
              }
              onDone(updated);
              return true;
            }}
            onFinish={() => setChargeDrawer(null)}
            onCancel={() => setChargeDrawer(null)}
          />
        )}
      </Drawer>
    </>
  );
};

export const LineActions: React.FC<{
  line: SalesOrderLine;
  onDone: (so: SalesOrderDetail) => void;
  // The estimate's lines, so "Substitute item" can offer a product picker instead of an id box.
  estimateLines?: EstimateLine[];
  // What the customer still owes on credit, in rupees. While this is above zero the wallet exit is
  // blocked server-side, so the action says so up front instead of letting the operator fill in a
  // form and get refused.
  outstandingCreditRupees?: string;
  // Fired when the operator picks "Return / Damage" — opens the Batch return/damage modal in
  // the parent, scoped to just this one line. The modal's own Mode toggle picks which flow runs
  // on submit (return pickup + QC vs direct damage CN).
  onReturnClick?: () => void;
  onRefundClick?: () => void;
}> = ({ line, onDone, estimateLines, outstandingCreditRupees, onReturnClick, onRefundClick }) => (
  <EscalationMenu
    size="small"
    onDone={onDone}
    fieldContext={{ estimateLines }}
    actions={[
      // Both money-back exits, shown ONLY on a line a deallocation handed quantity back on
      // (`refundable_qty > 0`). The refund itself is the operation that takes the qty off the
      // customer's order (via the Zoho invoice + CN pair), so a separate "Cancel qty" action
      // was redundant and has been removed.
      ...(Number(line.refundable_qty ?? 0) > 0
        ? [
            {
              key: "refund_wallet",
              permission: "oms.cancel_item",
              label:
                Number(outstandingCreditRupees ?? 0) > 0
                  ? "Refund to wallet (credit owing)"
                  : "Refund to wallet",
              confirm:
                Number(outstandingCreditRupees ?? 0) > 0
                  ? `This customer still owes ₹${outstandingCreditRupees} on credit, so the wallet is not the right destination yet — spendable balance must not go to someone carrying a debt on this order. If the amount fits inside that credit it will be taken off what they owe instead, and the wallet will be left alone; if it is larger, this is refused and you should refund to the customer first. Either way you will be told which happened.`
                  : "Move the money for this quantity into the customer's wallet, to spend on a future order. Raises a linked invoice and a credit note in Zoho — that pair is what cancels the quantity on the sales order — and the balance becomes spendable once the credit note lands, usually within a minute. Use Refund to customer instead when the money is going back to their bank.",
              fields: ["qty", "refund_reason_code", "notes"] as FieldKey[],
              run: (v: Record<string, any>) =>
                refundLine(line.id, {
                  qty: v.qty,
                  destination: "wallet",
                  reason_code: v.reason_code,
                  notes: v.notes,
                }),
            },
            ...(onRefundClick
              ? [
                  {
                    key: "refund_customer",
                    permission: "oms.cancel_item",
                    label: "Refund to customer",
                    danger: true,
                    fields: [] as FieldKey[],
                    custom: () => onRefundClick(),
                  },
                ]
              : []),
          ]
        : []),
      // Return is a post-delivery exit — only shown when the line has delivered qty that isn't
      // already in a return. A deallocated-only line (refundable_qty > 0, returnable_qty == 0)
      // has nothing physical to pick up, so the refund actions above are the correct exit there.
      // Post-delivery exit — one entry point for both Return (goods come back) and Damage
      // (customer keeps goods). The Return / Damage modal's Mode toggle picks which flow runs on
      // submit, so an operator doesn't have to decide BEFORE opening the dialog. Appears when
      // either returnable_qty or damageable_qty is > 0.
      ...(Number(line.returnable_qty ?? 0) > 0 || Number(line.damageable_qty ?? 0) > 0
        ? [
            {
              key: "return_damage",
              permission: "oms.return",
              label: "Return / Damage",
              fields: [] as FieldKey[],
              custom: () => onReturnClick?.(),
            },
          ]
        : []),
    ]}
  />
);

export const AllocationActions: React.FC<{
  allocation: Allocation;
  onDone: (so: SalesOrderDetail) => void;
  // The parent card's `so.lines` lookups, so the deallocation picker can name each allotment item by
  // product (AllocationItem itself carries only a line id). Optional — the picker falls back to the
  // line number when they aren't threaded down.
  lineProducts?: Map<number, ProductInfo | null>;
  lineUnits?: Map<number, string | null>;
}> = ({ allocation, onDone, lineProducts, lineUnits }) => {
  const actions: ActionDef[] = [];
  // Upgrade only applies to a backorder (a stock/dropship allocation has nothing to upgrade).
  if (allocation.shipment_type === "backorder") {
    actions.push({
      key: "upgrade",
      permission: "oms.upgrade",
      label: "P2P upgrade",
      confirm: "Ship direct from the supplier, skipping the MD-warehouse leg. The order stays regular (not express).",
      fields: ["reason"],
      run: (v) => upgradeP2P(allocation.id, v.reason || ""),
    });
  }
  // Deallocate — the ONE action that changes quantity. It replaced Split / Short-close and Record
  // damage, which were the same reduction under three names: the difference between them was only
  // whether the purchase order came down with the plan, which is now the `PO down` switch, and what
  // the quantity was for, which is now the reason code. Offered on EVERY allotment at every stage;
  // the backend decides what each ladder does from how far the goods have actually got.
  actions.push({
    key: "deallocate",
    permission: "oms.deallocate",
    label: "Deallocate (send back to desk)",
    confirm:
      "Pick how much of each item comes off, and WHERE THOSE GOODS END UP — the second answer decides how far back this unwinds. Back to the supplier rolls everything back, the purchase order with it. Into one of our warehouses leaves the PO standing: we own those units, so they still arrive and become MD stock, which is what damaged goods want. Only the places these goods have actually been are offered. The allotment then shrinks, its legs shrink with it (cancelled outright if it empties), and the line goes back on the AVAILABILITY DESK to be sourced again — nobody chooses a new supplier here. This does NOT touch the customer's order: cancel the quantity separately once it is off. If the goods have already been picked, packed or dispatched, the stack is taken apart and rebuilt at the new quantity — including the invoice — so tell the warehouse before you confirm. On a drop shipment each line comes off whole and only back to the supplier — other lines can stay; bringing dropshipped goods into a warehouse is a sales return instead.",
    fields: ["deallocation_items", "reason_code", "reason"],
    run: (v) => {
      const picks = (v.picks ?? {}) as Record<
        string,
        { qty?: number; take?: boolean; destination?: string }
      >;
      // A whole-line row (drop shipment) is a tick, not a quantity — resolve it to the line's full
      // allotted qty here, so everything below this point sees one shape.
      const fullQty = new Map(
        (allocation.items ?? []).map((i) => [String(i.id), Number(i.qty)])
      );
      const items = Object.entries(picks)
        .map(([allocationItemId, row]) => ({
          allocation_item_id: Number(allocationItemId),
          qty: row?.take
            ? fullQty.get(allocationItemId) ?? 0
            : Number(row?.qty ?? 0),
          destination: row?.destination as string,
        }))
        .filter((i) => i.qty > 0);
      const missing = items.filter((i) => !i.destination);
      if (missing.length > 0) {
        // The backend refuses this anyway, but saying it here keeps the modal open with the rows
        // still filled in rather than bouncing the operator back to an empty form.
        message.warning("Choose where the goods end up for every quantity you entered");
        return Promise.resolve(undefined);
      }
      if (items.length === 0) {
        // Nothing picked: keep the modal open (a resolved `undefined` leaves it up) and say why,
        // rather than posting an empty action for the backend to refuse.
        message.warning(
          allocation.shipment_type === "dropship"
            ? "Tick at least one line to take off"
            : "Enter a quantity on at least one item"
        );
        return Promise.resolve(undefined);
      }
      return deallocateAllocation(allocation.id, {
        items,
        reason_code: v.reason_code,
        reason: v.reason,
      });
    },
  });
  // Customisation Edit — the supplier re-measures their own delivery and only the BUY side moves.
  //
  // The label is deliberately oblique and the action sits low in the list. It is legitimate but
  // narrow: the one verb that makes the books disagree with the order ON PURPOSE, so what arrives
  // and what is billed no longer match. It is for the suppliers who genuinely cannot know the
  // quantity until they have cut/weighed/counted it — not a general "fix the numbers" tool, and not
  // the thing to reach for when a supplier simply short-supplied (that is a Deallocate).
  //
  // BACKORDER only. A stock allotment has no purchase order to re-state, and Zoho cannot amend a
  // drop-ship line in place at all — the backend refuses both with a readable 400, but there is no
  // reason to offer a button that can only ever earn one.
  if (allocation.shipment_type === "backorder") {
    actions.push({
      key: "modify-po",
      permission: "oms.modify_po",
      label: "Customisation Edit",
      confirm:
        "For suppliers who only know the quantity once they have measured it. Enter what the supplier ACTUALLY measured: it replaces the quantity on the purchase order (it is not an amount to take off) and may be higher or lower than what was ordered. The purchase order and the goods-inwards legs move to that figure, so the GRN reconciles and the order stops being stuck. THE CUSTOMER'S SIDE DOES NOT MOVE — the allotment, the package, the invoice and the e-way bill all stay at the ordered quantity, so what we receive and what we bill will differ and stock at the receiving warehouse will carry that gap. That is the intended outcome; if it is not what you want, this is the wrong action. Cannot be used once goods have been receipted.",
      fields: ["measured_items", "reason"],
      run: (v) => {
        const measured = (v.measured ?? {}) as Record<string, number | undefined>;
        // EVERY row is sent, including ones left at the pre-filled figure — do NOT skip a row
        // because it matches `po_qty`.
        //
        // `po_qty` is only half of what this action moves. It is the snapshot of the Zoho purchase
        // order; the goods-inwards LEGS are separate, and they are what the GRN reconciles against.
        // The stuck-order signature is precisely those two disagreeing — a supplier amends the PO in
        // Zoho (snapshot 117) while the legs still expect the ordered figure (133), and the receive
        // is then refused. Confirming 117 there is not a no-op: it IS the repair. An earlier version
        // filtered such rows out as "unchanged" and blocked the one case this exists for
        // (MD8202683290).
        //
        // Re-sending a figure the PO already carries is safe by design: the Zoho writer compares
        // against the live PO and skips the write, and the plan setter is absolute rather than a
        // delta, so it converges instead of compounding.
        const items = Object.entries(measured)
          .filter(([, qty]) => qty != null && Number.isFinite(Number(qty)))
          .map(([allocationItemId, qty]) => ({
            allocation_item_id: Number(allocationItemId),
            qty: Number(qty),
          }));
        if (items.length === 0) {
          message.warning("Enter the measured quantity for at least one item");
          return Promise.resolve(undefined);
        }
        if (items.some((i) => i.qty <= 0)) {
          // Zero is a deallocation, not a measurement: it has to release the quantity back to the
          // availability desk, which this action does not do. The backend refuses it too; saying it
          // here keeps the numbers the operator typed on screen.
          message.warning(
            "A measured quantity must be more than zero. To stop buying an item entirely, use Deallocate."
          );
          return Promise.resolve(undefined);
        }
        return modifyPo(allocation.id, { items, reason: v.reason });
      },
    });
  }
  // The two date moves, offered on every allocation: which one is legal depends on how far the goods
  // have physically got, which only the backend knows — it refuses with a readable 400.
  actions.push({
    key: "reschedule-pickup",
    permission: "oms.reschedule_pickup",
    label: "Reschedule pickup",
    confirm:
      "The goods will be ready on a different day. Moves the availability date and re-plans every open leg from it; the delivery date follows per the SLA (never earlier than a date the customer postponed to).",
    fields: ["availability_date", "reason"],
    run: (v) =>
      reschedulePickup(allocation.id, {
        new_date: isoDay(v.new_date),
        reason: v.reason,
      }),
  });
  actions.push({
    key: "reschedule-delivery",
    label: "Reschedule delivery",
    // Widened beyond admins: post_sales handles customer "deliver on another day" requests.
    permission: "oms.postpone_delivery",
    confirm:
      "The customer wants the goods on a different day. Moves the committed delivery date and the delivery leg, and re-books the truck. Everything else in the plan stays as it is.",
    fields: ["target_date", "reason"],
    run: (v) =>
      rescheduleDelivery(allocation.id, {
        target_date: isoDay(v.target_date),
        reason: v.reason,
      }),
  });
  // Reattempt applies to any allocation with a delivery leg (the backend refuses a cancelled or
  // never-dispatched one). ONE button, two flows, and the backend picks — the form asks which when it
  // opens and renders itself accordingly:
  //   · the delivery has not arrived → re-ship the whole load from the warehouse it is parked at,
  //     and everything else on the same truck moves with it;
  //   · the delivery DID arrive and goods came back on the truck → a fresh leg for THIS allotment
  //     alone, carrying only the items picked below.
  // Either way no new Zoho document is raised; only the e-way bill's Part-B (new vehicle) changes.
  // An optional planned date holds the booking to a chosen day; blank books the earliest slot.
  actions.push({
    key: "reattempt",
    permission: "oms.reattempt",
    label: "Reattempt delivery",
    confirm:
      "Send this allotment's goods out to the customer again from the warehouse they sit in now. No new Zoho docs — the original invoice still covers them, and only the e-way bill's Part-B (new vehicle) is updated.",
    fields: ["redelivery_items", "current_wh_id", "reattempt_date", "reason"],
    run: async (v) => {
      // Only the redelivery mode sends items; in repoint mode the field renders no inputs, so
      // `redelivery_picks` is absent and the whole load moves. A row trimmed to 0 is left with the
      // customer. Sending nothing at all would mean "everything", so an emptied form is refused here
      // rather than quietly re-shipping the lot.
      const picks = (v.redelivery_picks ?? {}) as Record<string, number | undefined>;
      const items = Object.entries(picks)
        .filter(([, qty]) => Number(qty) > 0)
        .map(([id, qty]) => ({ allocation_item_id: Number(id), qty: Number(qty) }));
      if (Object.keys(picks).length > 0 && items.length === 0) {
        message.error("Pick at least one item to send back out");
        return undefined;
      }
      const body = {
        current_wh_id: v.current_wh_id,
        target_delivery_date: v.reattempt_date ? isoDay(v.reattempt_date) : undefined,
        items: items.length > 0 ? items : undefined,
        reason: v.reason,
      };
      const res = await reattemptDelivery(allocation.id, body);
      if (res.status === "ok") return res.so;
      // Still on a live truck — the backend refused and asked to confirm. Collect a mandatory reason
      // and re-POST with confirm_in_transit; the delivery-team escalation is sent server-side.
      if (res.status === "needs_confirmation") {
        const reason = await confirmInTransitReattempt(res.detail);
        if (!reason) return undefined; // operator backed out
        const confirmed = await reattemptDelivery(allocation.id, {
          ...body,
          confirm_in_transit: true,
          reason,
        });
        return confirmed.status === "ok" ? confirmed.so : undefined;
      }
      return undefined; // real error (400 / network) — already surfaced
    },
  });
  // Only offered on an allocation actually stuck on the invoice (`error_kind === "invoice"`), which
  // is the same signal behind the "Invoice Error" tag. Legacy floors the payable to a whole rupee
  // while Zoho totals the lines and computes GST itself, so a fully-paid order can still show a few
  // paise outstanding and never release its shipment. Deliberately manual: adjusting an invoice is a
  // money decision, and if this appears on an order raised AFTER the automatic fix shipped, that is
  // worth a look rather than a click. The backend refuses anything that is not a rounding artefact.
  if (allocation.error_kind === "invoice") {
    actions.push({
      key: "settle-rounding",
      permission: "oms.settle_rounding",
      label: "Settle invoice rounding",
      confirm:
        "The customer has paid in full, but Zoho's invoice total is a few paise higher because we round the payable down and Zoho does not — so the shipment is held. This adds a rounding adjustment to the invoice so it settles, and releases the shipment. The taxable value and GST are unchanged, and a real shortfall will be refused.",
      fields: [],
      run: () => settleInvoiceRounding(allocation.id),
    });
  }
  // Re-send the vendor their PO (email + WhatsApp). Only meaningful once a PO exists on the leg —
  // the first send can silently no-op (backend notify switch off, or a Periskope/SendGrid blip), so
  // the desk needs a way to fire it again. A fire-and-inform action: it toasts its own outcome
  // (sent / logged-only / not-in-Zoho-yet) and does not mutate order state.
  if (allocation.po_number) {
    actions.push({
      key: "resend-supplier-notification",
      permission: "oms.resend_supplier_notification",
      label: "Resend supplier PO notification",
      confirm:
        "Re-send this PO to the vendor by email and WhatsApp. Use when the vendor didn't receive it. Nothing about the order changes — it only re-fires the notification.",
      fields: [],
      run: () => resendSupplierNotification(allocation.id),
    });
  }
  if (actions.length === 0) return null;
  return (
    <EscalationMenu
      size="small"
      onDone={onDone}
      actions={actions}
      fieldContext={{
        allocationItems: allocation.items,
        lineProducts,
        lineUnits,
        wholeLinesOnly: allocation.shipment_type === "dropship",
      }}
      redeliveryAllocationId={allocation.id}
      deallocationAllocationId={allocation.id}
    />
  );
};

// Whether this stage offers any escalation at all — the Actions cell asks before it decides to
// render an empty-state dash next to the document PDF links.
export const hasStageActions = (stage: OperationStage): boolean =>
  stage.operation_type === "purchase_receive" ||
  stage.operation_type === "pick" ||
  stage.operation_type === "service" ||
  stage.status === "error";

export const StageActions: React.FC<{
  stage: OperationStage;
  onDone: (so: SalesOrderDetail) => void;
}> = ({ stage, onDone }) => {
  const actions: ActionDef[] = [];
  // A short receipt is no longer its own action. The shortfall is a DEALLOCATION raised on the
  // allotment — same operation, one escalation type — and the PO is short-closed to what arrived by
  // the `PO down` switch. Nothing stage-anchored is offered here any more.
  if (stage.operation_type === "pick") {
    actions.push({
      key: "pick",
      permission: "oms.pick",
      label: "Confirm pick",
      confirm: "Mark the pick complete, unblocking PACKAGE.",
      fields: [],
      run: () => pickComplete(stage.id),
    });
  }
  if (stage.operation_type === "service") {
    actions.push({
      key: "service",
      permission: "oms.service",
      label: "Confirm service performed",
      // Deliberately blunt about the money: unlike a pick, which only unblocks the next leg, this
      // is the service's billing trigger — there is no dispatch to raise the invoice later.
      confirm:
        "Confirm the work was carried out (the installation happened, the site visit happened). This completes the leg and RAISES THE CUSTOMER'S INVOICE for it — a service has no dispatch, so this is what bills it. Only confirm once the work is actually done.",
      fields: ["notes"],
      run: (v) => serviceComplete(stage.id, v.notes),
    });
  }
  // Offered only while the stage is actually stuck: retry requeues the FAILED documents and
  // bookings behind it, which is exactly what puts a stage in ERROR. A doc that has spent its
  // attempts is terminal by design, so this is the only way back — use it once the underlying
  // cause (missing HSN code, unmapped warehouse, spent API quota) is genuinely fixed.
  if (stage.status === "error") {
    actions.push({
      key: "retry",
      permission: "oms.retry",
      label: "Retry stage",
      confirm:
        "Requeue every failed Zoho document and carrier booking for this leg. They are sent on the next background pass (within a minute) — refresh to see the outcome. Do this once the underlying cause is fixed, otherwise it will simply fail again.",
      fields: [],
      run: () => retryStage(stage.id),
    });
  }
  // No dash here — the Actions cell owns the empty state (it also renders the document PDFs).
  return (
    <EscalationMenu
      size="small"
      onDone={onDone}
      actions={actions}
    />
  );
};
